import { T, useGT } from 'gt-react';
import { PencilSimpleIcon, TrashIcon } from '@phosphor-icons/react';
import type { UploadLayout, UploadRule } from '@erp/rpc';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { UnreadableRuleList } from './UnreadableRuleList';
import { useRuleText } from './useRuleText';

function SupplierName({
  layoutId,
  layouts,
}: {
  layoutId: string | null;
  layouts: UploadLayout[];
}) {
  if (layoutId === null) {
    return (
      <T context="upload rule applies to every supplier layout">
        All suppliers
      </T>
    );
  }
  return <>{layouts.find(({ id }) => id === layoutId)?.name ?? '—'}</>;
}

interface RowProps {
  rule: UploadRule;
  layouts: UploadLayout[];
  /** Null hides the action: the viewer may not do it. */
  onEdit: (() => void) | null;
  onToggle: ((enabled: boolean) => void) | null;
  onDelete: (() => void) | null;
  isBusy: boolean;
}

/** One saved upload rule as a compact list item, with its actions. */
export function UploadRuleRow({
  rule,
  layouts,
  onEdit,
  onToggle,
  onDelete,
  isBusy,
}: RowProps) {
  const gt = useGT();
  const { columnLabel, describeCheck, severityNames } = useRuleText();
  return (
    <li className="flex items-center gap-2 border-b py-1.5 text-sm last:border-b-0">
      <Switch
        aria-label={gt('Rule on', {
          context:
            'label of the switch that turns an upload validation rule on or off',
        })}
        checked={rule.enabled}
        disabled={!onToggle || isBusy}
        onCheckedChange={(enabled) => onToggle?.(enabled)}
      />
      <div className="min-w-0 flex-1">
        <div className="truncate">
          {columnLabel(rule.column)} {describeCheck(rule.check)}
        </div>
        <div className="text-muted-foreground flex items-center gap-1 text-xs">
          <Badge
            variant={rule.severity === 'block' ? 'destructive' : 'secondary'}
          >
            {severityNames[rule.severity]}
          </Badge>
          <SupplierName layoutId={rule.layoutId} layouts={layouts} />
        </div>
      </div>
      {onEdit ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={onEdit}
          aria-label={gt('Edit rule', {
            context: 'button that edits an upload validation rule',
          })}
        >
          <PencilSimpleIcon className="h-4 w-4" />
        </Button>
      ) : null}
      {onDelete ? (
        <Button
          size="sm"
          variant="ghost"
          disabled={isBusy}
          onClick={onDelete}
          aria-label={gt('Delete rule', {
            context: 'button that deletes an upload validation rule',
          })}
        >
          <TrashIcon className="h-4 w-4" />
        </Button>
      ) : null}
    </li>
  );
}

/** Stored rules this version can't read, listed so they can be deleted. */
export function UnreadableRules(props: {
  ids: string[];
  onDelete: ((id: string) => void) | null;
  isBusy: boolean;
}) {
  return (
    <UnreadableRuleList
      {...props}
      warning={
        <T context="upload rules panel warning about stored rules this version cannot read">
          These rules were saved by another version and can’t be read. Downloads
          are paused until they are deleted.
        </T>
      }
      deleteLabel={
        <T context="button that deletes an unreadable upload validation rule">
          Delete
        </T>
      }
    />
  );
}
